/**
 * Financial visibility — the "who is allowed to see money" gate.
 *
 * WHAT IT DOES
 * ------------
 * Currency figures are hidden from every signed-in staff member by DEFAULT.
 * Two things unhide them:
 *
 *   1. Directors — anyone in `tenant.ownerIds` (same list that drives
 *      `isDirector()` / `useDirectorAccess()`). Always on, no toggle.
 *   2. An explicit per-user opt-in — `users/{userId}.canViewFinancials === true`,
 *      flipped by an admin in Settings → Financials.
 *
 * Everyone else sees `FINANCIAL_MASK` where an amount would render.
 *
 * ⚠️ THIS IS A DISPLAY GATE, NOT A DATA GATE. The underlying deal/invoice
 * documents are still readable by any signed-in user — the amounts simply
 * aren't rendered. The `canViewFinancials` FIELD itself is admin-write-only
 * (see the `/users/{userId}` block in firestore.rules), so a user cannot
 * unhide themselves; but a determined user with dev tools can still read the
 * raw numbers out of Firestore. Real enforcement needs per-field rules or a
 * server-side projection — tracked in KNOWN_ISSUES.md.
 *
 * WHY A localStorage CACHE
 * ------------------------
 * The money formatters in `src/utils/pricing.ts` are plain sync functions with
 * no React context (they're called from services, helpers and ~50 components).
 * They need a synchronous answer, so the resolved value is mirrored to
 * localStorage — exactly the pattern `cacheIsAdmin()` / `isAdminSync()` and
 * `cacheOwnerIds()` / `getOwnerIdsSync()` already use in `src/utils/access.ts`.
 * `FinancialVisibilityProvider` owns the write side and keeps it live.
 *
 * FAIL-CLOSED, WITH ONE DELIBERATE EXCEPTION
 * ------------------------------------------
 * An unset/unparseable cache denies. The exception is CUSTOMER-FACING
 * surfaces: the public proposal viewer, the payment page and the client plan /
 * variation viewers must ALWAYS show real prices — masking them would show a
 * customer `••••` where their quote total belongs. Those routes are detected
 * by path prefix (see CUSTOMER_FACING_PATH_PREFIXES) rather than by session,
 * because a staff member can open a customer link while signed in.
 */
import { getSession } from '@/lib/session';

/** Rendered in place of any currency amount the viewer may not see. */
export const FINANCIAL_MASK = '••••';

/** localStorage key the resolved `can view financials` answer is cached under. */
const CAN_VIEW_FINANCIALS_STORAGE_KEY = 'user_can_view_financials';

/**
 * The live, in-memory answer — set by FinancialVisibilityProvider during its
 * RENDER pass (not an effect), so the very first paint after the permission
 * resolves is already correct. Without it the localStorage mirror lags by one
 * commit and a director sees `••••` flash before their figures appear.
 *
 * `null` = nothing resolved this page-load; fall back to the localStorage
 * mirror (which survives reloads).
 */
let liveAnswer: boolean | null = null;

/**
 * Publish the live answer. Idempotent — safe to call on every render, and
 * safe under React 18 StrictMode's double-render.
 */
export function setLiveFinancialVisibility(canView: boolean | null): void {
  liveAnswer = canView;
}

/**
 * Route prefixes served to CUSTOMERS (no sign-in required). Money is never
 * masked here — see the module header. Keep in sync with the public routes
 * mounted outside `<AuthGuard>` in src/App.tsx.
 */
export const CUSTOMER_FACING_PATH_PREFIXES = ['/p/', '/i/', '/client/'] as const;

/**
 * Cache the resolved answer so the sync formatters can read it.
 * Called by FinancialVisibilityProvider on every resolution.
 */
export function cacheCanViewFinancials(canView: boolean): void {
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.setItem(
    CAN_VIEW_FINANCIALS_STORAGE_KEY,
    canView ? 'true' : 'false',
  );
}

/** Clear the cache (logout / user switch on a shared device). */
export function clearCanViewFinancialsCache(): void {
  liveAnswer = null;
  if (typeof window === 'undefined' || !window.localStorage) return;
  window.localStorage.removeItem(CAN_VIEW_FINANCIALS_STORAGE_KEY);
}

/**
 * Synchronous read of the resolved answer — the live in-memory value when the
 * provider has published one, else the localStorage mirror from a previous
 * page-load. Fail-closed: unset denies, so a cold start hides money until the
 * provider resolves rather than flashing real figures at the wrong person.
 */
export function canViewFinancialsSync(): boolean {
  if (liveAnswer !== null) return liveAnswer;
  if (typeof window === 'undefined' || !window.localStorage) return false;
  return (
    window.localStorage.getItem(CAN_VIEW_FINANCIALS_STORAGE_KEY) === 'true'
  );
}

/** True when the current URL is one of the customer-facing public routes. */
export function isCustomerFacingRoute(
  pathname: string | undefined = typeof window !== 'undefined'
    ? window.location.pathname
    : undefined,
): boolean {
  if (!pathname) return false;
  return CUSTOMER_FACING_PATH_PREFIXES.some((prefix) =>
    pathname.startsWith(prefix),
  );
}

/**
 * The single question every money render asks: should this amount be hidden?
 *
 * Masks only when ALL of:
 *   - we're not on a customer-facing route, AND
 *   - a staff session exists (a logged-out visitor is a customer, not staff), AND
 *   - the resolved permission says no.
 */
export function shouldMaskFinancials(): boolean {
  if (isCustomerFacingRoute()) return false;
  if (!getSession().userId) return false;
  return !canViewFinancialsSync();
}

/**
 * Resolve the permission from its two inputs. Pure — used by the provider and
 * directly unit-tested.
 *
 * @param isDirector  logged-in user is in `tenant.ownerIds`
 * @param userFlag    `users/{userId}.canViewFinancials`
 */
export function resolveFinancialVisibility(
  isDirector: boolean,
  userFlag: boolean | undefined | null,
): boolean {
  return isDirector || userFlag === true;
}
